import React, { useEffect, useRef }  from "react";
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {Box, Button, Typography} from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";

const DocumentUploadWidget = (props) => {
    const INITIAL_STATE = "Take new picture";
    const SECOND_STATE = "Capture image";
    const THIRD_STATE = "Retake picture";

    const videoRef = useRef();
    const canvasRef = useRef();
    const higherResCanvasRef = useRef();

    const setPhotoBlob = props.setPhotoBlob;
    const widgetState = props.widgetState;
    const setWidgetState = props.setWidgetState;
    const picturesOnly = !props.setFileList;
    const fileList = props.fileList;
    
    useEffect(() => {
        if(widgetState === INITIAL_STATE) {
            resetWidget();
        }
    },
        // eslint-disable-next-line
    [widgetState]);

    const resetWidget = () => {
        if(canvasRef) {
            canvasRef.current.hidden = false;
            videoRef.current.hidden = true;
            drawImageFromUrl(
                "https://res.cloudinary.com/treesource-systems/image/upload/v1706579588/tree_source_logo_300w.png",
                canvasRef,
                300,
                400);
        }
    }

    const initPhotoBlob = () => {
        higherResCanvasRef.current.toBlob((blob) => {
            setPhotoBlob(blob);
            //
            // const file = new File([blob], "captured_image.png", { type: "image/png" });
            // optionallySetFiles([file])
        }, "image/png");
    }

    const clearUploadData = () => {
        if(fileList && fileList.length > 0) {
            props.setFileList([]);
        }

        setPhotoBlob(null);
    }

    const optionallySetFiles = (files) => {
        if(props.setFileList) {
            props.setFileList(files);
        }
    }

    const handleFileUpload = (event) => {
        clearUploadData();

        const files = event.target.files;
        if(!files || files.length === 0) {
            return;
        }

        if(files.length === 1 && files[0].type.startsWith("image/")) {
            canvasRef.current.hidden = false;
            const url = URL.createObjectURL(files[0]);

            drawImageFromUrl(url, canvasRef, 300, 400, null);
            drawImageFromUrl(url, higherResCanvasRef, 900, 1200, initPhotoBlob);

            optionallySetFiles(files);
        } else {
            canvasRef.current.hidden = true;
            optionallySetFiles(files);
        }

        setWidgetState(THIRD_STATE);
    }

    const drawImageFromUrl = (url, canvasRef, width, height, callback) => {
        const img = new Image();
        img.onload = () => {
            const context = canvasRef.current.getContext('2d');
            context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); // Clear the canvas
            context.drawImage(img, 0, 0, width, height);

            if(callback) {
                callback();
            }
        };
        img.src = url;
        canvasRef.current.width = width;
        canvasRef.current.height = height;
    }

    const initializeCamera = () => {
        console.log("Initializing camera...");
        
        // First check if the browser supports mediaDevices
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            console.error("Browser doesn't support mediaDevices API");
            alert("Your browser doesn't support camera access");
            return;
        }

        // Stop any existing streams
        if (videoRef.current && videoRef.current.srcObject) {
            console.log("Stopping existing stream");
            const tracks = videoRef.current.srcObject.getTracks();
            tracks.forEach(track => {
                console.log("Stopping track:", track.label, "Enabled:", track.enabled, "State:", track.readyState);
                track.stop();
            });
            videoRef.current.srcObject = null;
        }

        navigator.mediaDevices.enumerateDevices().then(devices => {
            devices.forEach(device => {
                console.log("Device:", device.kind, device.label);
            })
        });
        // Try to access the camera with more detailed constraints
        navigator.mediaDevices.getUserMedia({
            video: {
                facingMode: "environment",
                width: { ideal: 900 },
                height: { ideal: 1200 }
            },
            audio: false
        })
        .then((stream) => {
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.hidden = false;
                canvasRef.current.hidden = true;
            }
        })
        .catch((err) => {
            console.error("Camera access error details:", {
                name: err.name,
                message: err.message,
                constraint: err.constraint,
                stack: err.stack
            });
            
            switch (err.name) {
                case 'NotAllowedError':
                    alert("Camera access was denied. Please check your camera permissions.");
                    break;
                case 'NotFoundError':
                    alert("No camera found on your device.");
                    break;
                case 'NotReadableError':
                    alert("Camera is already in use by another application.");
                    break;
                default:
                    alert(`Camera error: ${err.message}`);
            }
        });
    }

    const captureImage = () => {
        if (canvasRef.current && videoRef.current && higherResCanvasRef.current) {
            canvasRef.current.width = 300;
            canvasRef.current.height = videoRef.current.videoHeight / (videoRef.current.videoWidth / canvasRef.current.width);

            const context = canvasRef.current.getContext('2d');
            context?.drawImage(videoRef.current, 0, 0, canvasRef.current.width, videoRef.current.videoHeight / (videoRef.current.videoWidth / canvasRef.current.width));

            higherResCanvasRef.current.width = videoRef.current.videoWidth;
            higherResCanvasRef.current.height = videoRef.current.videoHeight;

            const hiddenContext = higherResCanvasRef.current.getContext('2d');
            hiddenContext?.drawImage(videoRef.current, 0, 0, higherResCanvasRef.current.width, videoRef.current.videoHeight);

            initPhotoBlob();

            if (videoRef.current.srcObject) {
                videoRef.current.srcObject.getVideoTracks().forEach(function(track) {
                    track.stop();
                });
            }

            canvasRef.current.hidden = false;
            videoRef.current.hidden = true;
        }
    }

    const transitionCamera = () => {
        clearUploadData()

        if(widgetState === INITIAL_STATE) {
            initializeCamera();
            setWidgetState(SECOND_STATE);
            return;
        } else if(widgetState === SECOND_STATE) {
            captureImage();
            setWidgetState(THIRD_STATE);
        } else if(widgetState === THIRD_STATE) {
            initializeCamera()
            setWidgetState(SECOND_STATE);
        }
    };

    const renderFilePreview = () => {
        if (fileList && fileList.length > 0) {
            return (
                <div style={{display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center"}}>
                    {Array.from(fileList).map((file, index) => {
                        return (
                            <div key={index}>
                                <Box key={index} style={{ display: 'flex', marginTop: 10, gap: 10 }}>
                                    <ListAltIcon />
                                    <Typography>{file.name}</Typography>
                                </Box>
                                {/*<img src={URL.createObjectURL(file)} alt="Uploaded" width="300" height="400" /> //forget me not lol*/}
                            </div>
                        );
                    })}
                </div>
            );
        }
    }

    return (
        <div style={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center"}}>
            <video 
                ref={videoRef} 
                width="300" 
                hidden 
                autoPlay 
                playsInline
                muted
            ></video>
            <canvas ref={canvasRef} width="300" height="400" hidden></canvas>
            <canvas ref={higherResCanvasRef} width="900" height="1200" hidden></canvas>
            {renderFilePreview()}
            <Button
                style={{
                    marginTop: 10,
                    marginBottom: 10,
                    width: "100%",
                    flex: "1",
                }}
                startIcon={<PhotoCameraIcon />}
                id="useCameraButton"
                type="submit"
                variant="contained"
                color="primary"
                onClick={() => transitionCamera()}
            >
                {widgetState}
            </Button>
            <Button
                style={{
                    marginBottom: 10,
                    width: "100%",
                    flex: "1",
                }}
                id="useFileButton"
                component="label"
                variant="contained"
                color="primary"
                startIcon={<CloudUploadIcon />}
                disabled={widgetState === SECOND_STATE}
                onChange={(event) => handleFileUpload(event)}
            >
                Upload From File
                <input
                    type="file"
                    accept={picturesOnly ? "image/*" : "image/*,.xlsx,.xls,.csv,.pdf,.doc,.docx,.txt"}
                    hidden
                />
            </Button>
        </div>
    );
}

export default DocumentUploadWidget;